import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Search, ArrowRight } from "lucide-react";

import { createAgencyFolder } from "../../services/api";
import { taskboard } from "../../services/taskboardService";

// ──────────────────────────────────────────────────────────────────────
// CreateFolderModal — roster picker (product rule 8). Players who ALREADY
// have a folder show "Folder exists — open it" (a direct affordance to
// jump into it) and cannot be selected again, so duplicate creation is
// blocked in the UI as well as by the backend's unique(agency, player).
// One folder per player per agency.
//
// Matching a roster player to an existing folder is done by any stable
// id the API exposes, falling back to a normalized name — enough to flag
// the duplicate; the backend remains the source of truth (returns the
// existing folder with 200 if a race slips through).
// ──────────────────────────────────────────────────────────────────────

function playerKeys(p) {
  return [p?.id, p?.player_id, p?.wyscout_id, p?.account_id]
    .filter((v) => v != null)
    .map((v) => `id:${v}`)
    .concat(p?.name ? [`name:${p.name.trim().toLowerCase()}`] : []);
}

const CreateFolderModal = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [folderByKey, setFolderByKey] = useState({});
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelectedPlayerId("");
    setSearch("");
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [playersRes, foldersRes] = await Promise.all([
        taskboard.getRosterPlayers({ page: 1, limit: 200 }),
        taskboard.getFolders(),
      ]);
      if (cancelled) return;
      const list = Array.isArray(playersRes?.data?.results)
        ? playersRes.data.results
        : Array.isArray(playersRes?.data?.data)
          ? playersRes.data.data
          : [];
      setPlayers(list);
      // Map every key a folder's player might match on -> folder id.
      const map = {};
      (foldersRes.data || []).forEach((f) => {
        playerKeys(f.player).forEach((k) => { map[k] = f.id; });
      });
      setFolderByKey(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const existingFolderId = (p) => {
    for (const k of playerKeys(p)) if (folderByKey[k] != null) return folderByKey[k];
    return null;
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return players;
    return players.filter(
      (p) => (p.name || "").toLowerCase().includes(needle) || (p.position || "").toLowerCase().includes(needle),
    );
  }, [players, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlayerId) {
      toast.error("Pick a player first");
      return;
    }
    setSubmitting(true);
    const response = await createAgencyFolder(selectedPlayerId);
    setSubmitting(false);
    if (!response.success) {
      toast.error(response.error || "Failed to create folder");
      return;
    }
    toast.success(
      response.created
        ? "Folder created"
        : response.restored
          ? "Folder restored, opening…"
          : "Folder already exists, opening…",
    );
    onSuccess && onSuccess(response.data);
    onClose && onClose();
  };

  const openExisting = (folderId) => {
    onClose && onClose();
    navigate(`/agency/folders/${folderId}`);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h2 className="text-xl font-semibold text-[#0B0B0B]">Create folder</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 pb-3">
            <p className="text-sm text-[#535370] mb-4">
              Pick a player from your roster. A folder lets you log deal-tracker
              entries and attach links. One folder per player.
            </p>
            <div className="flex items-center gap-2 border border-[#E9E9E9] rounded-lg px-3 py-2">
              <Search className="w-4 h-4 text-[#98A2B3] flex-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or position"
                className="w-full text-sm outline-none placeholder:text-[#98A2B3]"
              />
            </div>
          </div>

          <div className="px-5 flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-center text-[#6B7280] py-8">Loading players…</p>
            ) : filtered.length === 0 ? (
              <p className="text-center text-[#6B7280] py-8">
                {search.trim() ? "No matches" : "No players found. Add one to your roster first."}
              </p>
            ) : (
              <ul className="divide-y divide-[#F2F4F7]">
                {filtered.map((p) => {
                  const folderId = existingFolderId(p);
                  const exists = folderId != null;
                  return (
                    <li key={p.id}>
                      {exists ? (
                        <div className="flex items-center gap-3 py-3 opacity-90">
                          {p.image ? (
                            <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-secondary text-sm font-medium">
                              {(p.name || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[14px] font-medium text-[#0B0B0B] truncate">{p.name || "(no name)"}</span>
                            <span className="text-[12px] text-[#98A2B3]">Folder exists</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => openExisting(folderId)}
                            className="flex items-center gap-1 text-[13px] font-semibold text-[#1866CC] hover:underline flex-none"
                          >
                            Open it <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-3 py-3 cursor-pointer hover:bg-[#F8F9FB] -mx-2 px-2 rounded">
                          <input type="radio" name="player" value={p.id} checked={String(selectedPlayerId) === String(p.id)} onChange={() => setSelectedPlayerId(p.id)} />
                          {p.image ? (
                            <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-secondary text-sm font-medium">
                              {(p.name || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex flex-col min-w-0">
                            <span className="text-[14px] font-medium text-[#0B0B0B] truncate">{p.name || "(no name)"}</span>
                            <span className="text-[12px] text-[#6B7280] truncate">
                              {[p.position, p.current_club?.name].filter(Boolean).join(" · ") || "-"}
                            </span>
                          </div>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB]">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 border border-[#E9E9E9] rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting || !selectedPlayerId} className="px-4 py-2 bg-[#2283FF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Creating…" : "Create folder"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CreateFolderModal;
